import { ArrowUpRight } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

// Logo Apple ufficiale (silhouette con foglia), usato nei badge App Store
const AppleLogo = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701" />
  </svg>
);

// Logo Google Play ufficiale (freccia multicolore)
const GooglePlayLogo = ({ className }) => (
  <svg viewBox="30 336.7 120.9 129.2" className={className} aria-hidden="true">
    <path fill="#00F076" d="M119.2,421.2c15.3-8.4,27-14.8,28-15.3c3.2-1.7,6.5-6.2,0-9.7c-2.1-1.1-13.4-7.3-28-15.3l-20.1,20.2L119.2,421.2z" />
    <path fill="#FF3A44" d="M99.1,401.1l-64.2,64.7c1.5,0.2,3.2-0.2,5.2-1.3c4.2-2.3,48.8-26.7,79.1-43.3L99.1,401.1L99.1,401.1z" />
    <path fill="#FFCE00" d="M99.1,401.1l20.1-20.2c0,0-74.6-40.7-79.1-43.1c-1.7-1-3.6-1.3-5.3-1L99.1,401.1z" />
    <path fill="#00D9FF" d="M99.1,401.1l-64.3-64.3c-2.6,0.6-4.8,2.9-4.8,7.6c0,7.5,0,107.5,0,113.8c0,4.3,1.7,7.4,4.9,7.7L99.1,401.1z" />
  </svg>
);

export const StoreBadges = ({ app, compact = false }) => {
  const { t } = useLanguage();
  return (
    <div className="flex flex-wrap items-center gap-3">
      {app.storeUrl ? (
        <a
          href={app.storeUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`store-badge-apple-${app.slug}`}
          className="group inline-flex items-center gap-3 rounded-xl border border-white/15 bg-white/[0.03] px-5 py-3 transition-colors duration-300 hover:border-neon"
        >
          <AppleLogo className="h-6 w-6" />
          <span className="flex flex-col leading-none">
            <span className="text-[10px] uppercase tracking-wider text-muted2">{compact ? "App Store" : "Download on the"}</span>
            {!compact && <span className="mt-1 font-display text-base font-bold">App Store</span>}
          </span>
          <ArrowUpRight className="h-4 w-4 text-muted2 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-neon" />
        </a>
      ) : (
        <span
          data-testid={`store-badge-apple-soon-${app.slug}`}
          className="inline-flex cursor-not-allowed items-center gap-3 rounded-xl border border-dashed border-white/10 px-5 py-3 opacity-50"
        >
          <AppleLogo className="h-5 w-5" />
          <span className="font-mono text-[11px] uppercase tracking-widest">{t.showcase.soonStore}</span>
        </span>
      )}
      {app.appleOnly ? null : app.playUrl ? (
        <a
          href={app.playUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`store-badge-play-${app.slug}`}
          className="group inline-flex items-center gap-3 rounded-xl border border-white/15 bg-white/[0.03] px-5 py-3 transition-colors duration-300 hover:border-neon"
        >
          <GooglePlayLogo className="h-6 w-6" />
          <span className="flex flex-col leading-none">
            <span className="text-[10px] uppercase tracking-wider text-muted2">{compact ? "Google Play" : "Get it on"}</span>
            {!compact && <span className="mt-1 font-display text-base font-bold">Google Play</span>}
          </span>
          <ArrowUpRight className="h-4 w-4 text-muted2 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-neon" />
        </a>
      ) : (
        <span
          data-testid={`store-badge-play-${app.slug}`}
          className="inline-flex cursor-not-allowed items-center gap-3 rounded-xl border border-dashed border-white/10 px-5 py-3 opacity-50"
        >
          <GooglePlayLogo className="h-5 w-5" />
          <span className="font-mono text-[11px] uppercase tracking-widest">{t.showcase.play}</span>
        </span>
      )}
    </div>
  );
};
